import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import BlockIcon from '@mui/icons-material/Block';
import {
  SupplierIntegrationRequestsQuery,
  SupplierIntegrationStatus,
  useDisableAutomatedFollowUpsMutation,
} from '../../generated/graphql';

type Request =
  SupplierIntegrationRequestsQuery['supplierIntegrationRequests'][number];

interface Props {
  requests: Request[];
}

const STATUS_LABELS: Record<SupplierIntegrationStatus, string> = {
  [SupplierIntegrationStatus.FirstRequestSent]: '1st request sent',
  [SupplierIntegrationStatus.SecondRequestSent]: '2nd request sent',
  [SupplierIntegrationStatus.ThirdRequestSent]: '3rd request sent',
  [SupplierIntegrationStatus.ManualInterventionNeeded]:
    'Manual intervention needed',
};

const STATUS_COLORS: Record<
  SupplierIntegrationStatus,
  'default' | 'primary' | 'warning' | 'error'
> = {
  [SupplierIntegrationStatus.FirstRequestSent]: 'primary',
  [SupplierIntegrationStatus.SecondRequestSent]: 'primary',
  [SupplierIntegrationStatus.ThirdRequestSent]: 'warning',
  [SupplierIntegrationStatus.ManualInterventionNeeded]: 'error',
};

function formatNextEmailIn(days: number | null | undefined): string {
  if (days == null) return '–';
  if (days === 0) return 'today';
  if (days === 1) return 'in 1 day';
  return `in ${days} days`;
}

interface DisableConfirmDialogProps {
  open: boolean;
  loading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const DisableConfirmDialog = ({
  open,
  loading,
  onConfirm,
  onClose,
}: DisableConfirmDialogProps) => (
  <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm">
    <DialogTitle>Disable automated follow-up emails?</DialogTitle>
    <DialogContent>
      <DialogContentText>
        This will disable all automated follow-up emails for this integration
        request. This action cannot be reverted. Do you want to proceed?
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        color="error"
        variant="contained"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={16} /> : undefined}
      >
        Disable follow-ups
      </Button>
    </DialogActions>
  </Dialog>
);

interface StatusCellProps {
  request: Request;
  onDisableClick: (requestId: string) => void;
}

const StatusCell = ({ request, onDisableClick }: StatusCellProps) => {
  const hasPendingAutomatedFollowUps =
    request.status === SupplierIntegrationStatus.FirstRequestSent ||
    request.status === SupplierIntegrationStatus.SecondRequestSent;

  const isFollowUpsStopped =
    request.isActive ||
    Boolean(request.followUpsManuallyDisabledAt) ||
    Boolean(request.supplierReplyDetectedAt);

  const canDisable =
    hasPendingAutomatedFollowUps &&
    !request.isActive &&
    !request.followUpsManuallyDisabledAt &&
    !request.supplierReplyDetectedAt;

  let followUpIcon: React.ReactNode = null;
  if (isFollowUpsStopped && request.automatedFollowUpsStopTooltip) {
    followUpIcon = (
      <Tooltip title={request.automatedFollowUpsStopTooltip} arrow>
        <BlockIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      </Tooltip>
    );
  } else if (canDisable) {
    followUpIcon = (
      <Tooltip title="Disable automated follow-up emails" arrow>
        <Box
          component="span"
          sx={{ display: 'inline-flex', cursor: 'pointer' }}
          onClick={() => onDisableClick(request.id)}
        >
          <BlockIcon
            fontSize="small"
            sx={{
              color: 'error.main',
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Chip
        label={STATUS_LABELS[request.status]}
        color={STATUS_COLORS[request.status]}
        size="small"
        variant="outlined"
      />
      {followUpIcon}
    </Box>
  );
};

export const IntegrationRequestsTable = ({ requests }: Props) => {
  const [confirmDialogRequestId, setConfirmDialogRequestId] = useState<
    string | null
  >(null);

  const [disableFollowUps, { loading: disabling }] =
    useDisableAutomatedFollowUpsMutation();

  const handleDisableConfirm = async () => {
    if (!confirmDialogRequestId) return;
    try {
      const result = await disableFollowUps({
        variables: { requestId: confirmDialogRequestId },
        toastConfig: null,
      });
      if (result && !result.errors?.length) {
        setConfirmDialogRequestId(null);
      }
    } catch {
      // error surfaced via Apollo error handling
    }
  };

  if (requests.length === 0) {
    return (
      <Box py={4} textAlign="center">
        <Typography color="text.secondary" variant="body2">
          No integration requests found for this venue. Click &quot;Request New
          Integrations&quot; to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Supplier</TableCell>
              <TableCell>Integration Active</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Contact Email</TableCell>
              <TableCell>Next follow-up</TableCell>
              <TableCell>Last Email Sent</TableCell>
              <TableCell>Initiated By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((req) => (
              <TableRow key={req.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {req.globalSupplier.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  {req.isActive ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      color="success.main"
                    >
                      <CheckCircleOutlineIcon fontSize="small" />
                      <Typography variant="body2" color="success.main">
                        Yes
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      color="text.secondary"
                    >
                      <CancelOutlinedIcon fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        No
                      </Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <StatusCell
                    request={req}
                    onDisableClick={setConfirmDialogRequestId}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{req.contactEmail}</Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color={
                      req.nextEmailInDays != null
                        ? 'text.primary'
                        : 'text.secondary'
                    }
                  >
                    {formatNextEmailIn(req.nextEmailInDays)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(req.lastContactDate).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {req.initiatedByEmail}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DisableConfirmDialog
        open={Boolean(confirmDialogRequestId)}
        loading={disabling}
        onConfirm={handleDisableConfirm}
        onClose={() => setConfirmDialogRequestId(null)}
      />
    </>
  );
};
